import { CSSProperties } from 'react';
import { Column, ColumnDef, VisibilityState } from '@tanstack/react-table';
import { cn } from '@anytag/shared-utils';

// Multiple sticky columns: https://tanstack.com/table/v8/docs/framework/react/examples/column-pinning-sticky?panel=sandbox
export const getCommonPinningStyles = <TData, TValue>(column: Column<TData, TValue>, isOverflow: boolean) => {
  // Just support left pinning for now
  const isPinned = column.getIsPinned() === 'left';
  const isLastLeftPinnedColumn = isPinned && column.getIsLastColumn('left');

  return {
    isPinned,
    styles: {
      width: column.getSize(),
      left: isPinned ? `${column.getStart('left')}px` : undefined
    } satisfies CSSProperties,
    pinningClassName: isPinned
      ? cn(
          { 'sticky [&[data-slot="table-cell"]]:z-1 [&[data-slot="table-head"]]:z-2': isPinned },
          { '[&+[data-slot="table-head"]]:pl-4': isLastLeftPinnedColumn }, // add padding for next column
          { '[&+[data-slot="table-cell"]]:pl-4': isLastLeftPinnedColumn }, // add padding for next column
          // When last left pinned column and table is not overflow, we add a right border to the last left pinned column
          { 'cell-right-border z-2 [&+[data-slot="table-head"]]:pl-4!': isLastLeftPinnedColumn && !isOverflow },
          // When last left pinned column and table is overflow, we add a box-shadow to the last left pinned column
          {
            'after:absolute after:top-0 after:-right-[var(--gradient-width)]! after:h-full after:w-[var(--gradient-width)]! after:bg-[linear-gradient(270deg,transparent_0%,#6E7C89_100%)] after:opacity-10 after:content-[""]':
              isLastLeftPinnedColumn && isOverflow
          }
        )
      : undefined
  };
};

export const generateVisibilityColumnState = <TData, TValue>(
  columns: ColumnDef<TData, TValue>[],
  hasColumnCustomization = false
) =>
  columns.reduce<VisibilityState>((acc, curr) => {
    if (!('accessorKey' in curr) || !curr.accessorKey) return acc;

    // Hide column from all table functions (visiable columns, customization list, etc)
    if (curr.meta?.hidden) {
      return { ...acc, [curr.accessorKey]: false };
    }

    if (hasColumnCustomization) {
      return {
        ...acc,
        // If has column customization, all columns are hidden by default
        // The columns which has `enableHiding: false` will be set as default visible columns
        [curr.accessorKey]: curr.enableHiding === false
      };
    }

    // If no column customization, do nothing because all columns are visible by default
    return acc;
  }, {});

// Filter only the columns that are visible
export const getVisibleColumns = (visibilityColumn: VisibilityState): VisibilityState =>
  Object.fromEntries(Object.entries(visibilityColumn).filter(([, value]) => value === true));
